import { formFromDraftPayload, type WizardSeoFields } from "@/lib/wizard-seo";

/**
 * Draft-owned create-wizard state. Hydration always starts from blanks.
 * A missing payload field uses the default. A present empty value stays empty.
 */

export interface DraftVariationState {
  options: unknown[];
  variants: unknown[];
  combinationCount: number;
  duplicateSkus: unknown[];
  hasData: boolean;
  /** Optional authored SKU state retained when resuming an existing draft. */
  manualSkus?: string[];
  skuPattern?: string;
}

export interface DraftStockRow {
  location_id: string;
  qty: string;
  unit_cost: string;
  reference: string;
  variant_sku: string;
}

export interface CreateDraftOwnedState<TForm, TIntent, TMedia> {
  form: TForm;
  variation: DraftVariationState;
  openingStock: DraftStockRow[];
  stockChoice: "none" | "record";
  simpleQty: string;
  matrixLocationId: string;
  stockByLocation: boolean;
  sellingIntents: TIntent[];
  media: TMedia[];
  enrichError: string;
}

export function blankDraftVariation(): DraftVariationState {
  return { options: [], variants: [], combinationCount: 0, duplicateSkus: [], hasData: false };
}

export function blankCreateDraftState<TForm, TIntent, TMedia>(blankForm: TForm): CreateDraftOwnedState<TForm, TIntent, TMedia> {
  return {
    form: blankForm,
    variation: blankDraftVariation(),
    openingStock: [],
    stockChoice: "none",
    simpleQty: "",
    matrixLocationId: "",
    stockByLocation: false,
    sellingIntents: [],
    media: [],
    enrichError: "",
  };
}

function hasField(payload: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(payload, key);
}

export function hydrateCreateDraftState<TForm extends WizardSeoFields, TIntent, TMedia>(
  blankForm: TForm,
  payload: object | null | undefined,
  media: TMedia[] | null | undefined,
): CreateDraftOwnedState<TForm, TIntent, TMedia> {
  const blank = blankCreateDraftState<TForm, TIntent, TMedia>(blankForm);
  const source = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
  const variation = hasField(source, "variation") && source.variation && typeof source.variation === "object"
    ? source.variation as DraftVariationState
    : blank.variation;
  return {
    form: formFromDraftPayload(blankForm, (hasField(source, "form") ? source.form : undefined) as Partial<TForm> | undefined),
    variation,
    openingStock: hasField(source, "openingStock") && Array.isArray(source.openingStock)
      ? source.openingStock as DraftStockRow[]
      : [],
    stockChoice: source.stockChoice === "record" ? "record" : "none",
    simpleQty: typeof source.simpleQty === "string" ? source.simpleQty : "",
    matrixLocationId: typeof source.matrixLocationId === "string" ? source.matrixLocationId : "",
    stockByLocation: source.stockByLocation === true,
    sellingIntents: hasField(source, "sellingIntents") && Array.isArray(source.sellingIntents)
      ? source.sellingIntents as TIntent[]
      : [],
    media: Array.isArray(media) ? media : [],
    enrichError: "",
  };
}
